import { Delete } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
} from "@mui/material";
// Accordion components removidos
import SearchIcon from "@mui/icons-material/Search";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as React from "react";
import {
  deleteDocuments,
  getDocuments,
} from "../../../services/documents.service";
import { getAllEvents } from "../../../services/event.service";
// import { ModalComponent, useModal } from "../../../ui-components/Modal";
import { parseDate } from "../../../utils/parseDate";

import SectionTitle from "../../../ui-components/SectionTitle";
// import { adminButtonPrimarySx } from "../modalStyle";
import Dropzone from "../../../ui-components/Dropzone";
import { useToast } from "../../../ui-components/ToastProvider";
import { useConfirm } from "../../../ui-components/ConfirmProvider";

export default function Documents() {
  const [events, setEvents] = React.useState([]);
  const [documents, setDocuments] = React.useState([]);
  // const { open, handleClose, handleOpen } = useModal();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [eventSearch, setEventSearch] = React.useState("");
  const [pendingUploads, setPendingUploads] = React.useState<
    Record<number, File[]>
  >({});
  const [uploadProgress, setUploadProgress] = React.useState<
    Record<number, number[]>
  >({});
  const [uploadErrors, setUploadErrors] = React.useState<
    Record<number, string[]>
  >({});
  const allowedTypes = /^(image\/.*|application\/pdf)$/;
  const maxSizeMB = 20;
  const [selectedTab, setSelectedTab] = React.useState(0);
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const filteredEvents = React.useMemo(() => {
    const q = eventSearch.trim().toLowerCase();
    return events.filter((ev: any) => {
      if (!q) return true;
      const tabLabel = `${ev?.name ?? ""} - ${ev?.starts_at ? parseDate(ev.starts_at) : ""}`.toLowerCase();
      return tabLabel.includes(q);
    });
  }, [events, eventSearch]);

  React.useEffect(() => {
    if (filteredEvents.length === 0) {
      setSelectedTab(0);
    } else if (selectedTab >= filteredEvents.length) {
      setSelectedTab(0);
    }
  }, [filteredEvents, selectedTab]);

  function getDocs() {
    getDocuments().then((data) => {
      setDocuments(data);
    });
  }

  React.useEffect(() => {
    getDocs();
  }, []);

  React.useEffect(() => {
    getAllEvents().then((data) => {
      setEvents(data);
    });
  }, []);

  // Lógica de expansão de accordion removida

  const handleDelete = async (id: number) => {
    await deleteDocuments(id);
    window.location.reload();
  };

  const addFiles = (eventId: number, files: FileList | File[]) => {
    const asArray: File[] = Array.isArray(files) ? files : Array.from(files);
    const valid: File[] = [];
    const errors: string[] = [];
    asArray.forEach((file) => {
      if (!allowedTypes.test(file.type)) {
        errors.push(`${file.name}: tipo não permitido`);
      } else if (file.size > maxSizeMB * 1024 * 1024) {
        errors.push(`${file.name}: maior que ${maxSizeMB}MB`);
      } else {
        valid.push(file);
      }
    });
    if (valid.length) {
      setPendingUploads((prev) => ({ ...prev, [eventId]: valid }));
      setUploadProgress((prev) => ({ ...prev, [eventId]: valid.map(() => 0) }));
      uploadPendingFilesWithProgress(eventId, valid);
    }
    if (errors.length) {
      setUploadErrors((prev) => ({
        ...prev,
        [eventId]: [...(prev[eventId] || []), ...errors],
      }));
    }
  };

  const uploadPendingFilesWithProgress = async (
    eventId: number,
    filesParam?: File[]
  ) => {
    const files = filesParam ?? pendingUploads[eventId] ?? [];
    if (!files.length) return;
    try {
      await Promise.all(
        files.map(
          (file, idx) =>
            new Promise<void>((resolve, reject) => {
              const formData = new FormData();
              formData.append(String(eventId), file);
              const xhr = new XMLHttpRequest();
              xhr.open("POST", `${process.env.REACT_APP_API_URL}/documents`);
              xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                  const percent = Math.round((e.loaded / e.total) * 100);
                  setUploadProgress((prev) => {
                    const next = { ...prev };
                    const arr = [...(next[eventId] || [])];
                    arr[idx] = percent;
                    next[eventId] = arr;
                    return next;
                  });
                }
              };
              xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) resolve();
                else reject(new Error("Falha no upload"));
              };
              xhr.onerror = () => reject(new Error("Falha no upload"));
              xhr.send(formData);
            })
        )
      );
      await getDocs();
      setPendingUploads((prev) => {
        const next = { ...prev };
        delete next[eventId];
        return next;
      });
      setUploadProgress((prev) => {
        const next = { ...prev };
        delete next[eventId];
        return next;
      });
      setUploadErrors((prev) => {
        const next = { ...prev };
        delete next[eventId];
        return next;
      });
    } catch (err) {
      console.error(err);
      showToast("Falha ao enviar arquivos. Tente novamente.", { severity: "error" });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
          mb: 2,
        }}
      >
        <SectionTitle title="Documentos" />
        <TextField
          placeholder="Pesquisar eventos"
          value={eventSearch}
          onChange={(e) => setEventSearch(e.target.value)}
          size="small"
          sx={{ width: { xs: "100%", md: 360 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(16,24,40,.06)",
          mb: 3,
        }}
      >
        {filteredEvents.length > 0 ? (
          <>
            <Tabs
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons
              allowScrollButtonsMobile
              sx={{ mb: 2 }}
            >
              {filteredEvents.map((ev) => (
                <Tab
                  key={ev.id}
                  label={`${ev.name} - ${parseDate(ev.starts_at)}`}
                />
              ))}
            </Tabs>
            {(() => {
              const event = filteredEvents[selectedTab];
              if (!event) return null;
              return (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Dropzone
                      onAddFiles={(files) => addFiles(event.id, files)}
                      accept="image/*,application/pdf"
                      title="Arraste e solte documentos"
                      subtitle={`ou clique para selecionar. Máx: ${maxSizeMB}MB.`}
                      buttonLabel="Selecionar arquivos"
                    />
                    {!!uploadErrors[event.id]?.length && (
                      <Box sx={{ mt: 1, color: "error.main" }}>
                        {uploadErrors[event.id].map((err, i) => (
                          <div key={i}>{err}</div>
                        ))}
                      </Box>
                    )}
                    {!!uploadProgress[event.id]?.length && (
                      <Box sx={{ mt: 1 }}>
                        {uploadProgress[event.id].map((p, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={p}
                              sx={{ flex: 1 }}
                            />
                            <span>{p}%</span>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  {documents && documents.filter((doc) => doc.event_id === event.id).length > 0 ? (
                    <ImageList cols={isMobile ? 2 : 4}>
                      {documents
                        .filter((doc) => doc.event_id === event.id)
                        .map((item) => (
                          <a
                            key={item.id}
                            target={"_blank"}
                            rel="noreferrer"
                            href={item.url}
                          >
                            <ImageListItem>
                              <img
                                src={
                                  item.file_type.includes("image/")
                                    ? item.url
                                    : "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAbFBMVEX///8AAABLS0vc3NwwMDDo6Oi0tLTCwsJ0dHSGhoY9PT0/Pz/09PQcHBx4eHg4ODhVVVUUFBTIyMiXl5eqqqqhoaFtbW3S0tLOzs4uLi7i4uJlZWUlJSVfX19QUFCsrKwYGBiAgIBFRUW6urrFevtnAAADDElEQVR4nO3d61IaQRRF4eGiEMMtQAAxaozv/46JSf50M1xK9j5ntNb3ANOs6p7pES27aQAAAAAAAAAAAD685WrWnw9F7vvZObXlQ0/sLjupsP6m7utW4n5o6OtS4ndP3x9fs9P+2dgCO5K4MAZ2ItE5g2/S70XfPdiRxIE9MHuhmraJ7iTeRgSmJv6IKcxLHAUF5iU+1h/k6XV7c51jz+akxOpTTLfXX/LonZ2SuKs+xERwzePProx9sVpRghk8+XROSBwXH+BJcs1T+0/8Qn0qxn+WXPPkDhs+i339Ij3zDhE9i2XhQHLNM29JwbOYUBg8ixmFsbPoLxzPc2fRX/ilafmWMnAWIwonLT++xCVGFDaTzFkMKWwm07zEmMLWxKDHTVBh4ixGFebNYlhhM/mZM4txhVkLNbCwddOYS0Y8JbKwdRbtiaGFKVt/bGHGvRhcmDCL0YXxm0Z4YXhifGHT3Icm+gvvZuPS7OBXJdbElO9p2tj2xc4U9oaSsQ91p9C1UDtU2HuUjF7rUqHnbzUpfA8KKdSi8D0opFDLUXgzutT6gxZebv/pCwcUClDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaEChV4UKlDoRaHC5y8sT3jcWcY4rjwFVXOCZq08N35tGeO48j9DLixjvASsk+N+FaO/WMaozpKNXabVUb2eFbQtB5GcB3ypSTW25hTUA9UoijOdL7SrD2QxjfNQDdNbXH0u9yW2t/164I2pcFkPlMb2DGg5EyWF63yLpnnNTvtvZCtsWs56TeA8KXBwfvgAe2NhveunML8vbrL7ejNvYP3+Hc9zwkwhdxbH/sDcezHoZ7ZB1s4/tz5FC+uWowntps9hfW9GB6/hZptlaN9fy9WsPx/6zfuzVfS3QgAAAAAAAAAAAAa/ARXQN87ZDmHrAAAAAElFTkSuQmCC"
                                }
                                alt={item.title}
                                loading="lazy"
                              />
                              <ImageListItemBar
                                title={item.name}
                                subtitle={item["file_type"]}
                                actionIcon={
                                  <IconButton
                                    sx={{ color: "rgba(255, 255, 255, 0.54)" }}
                                    aria-label={`info about ${item.title}`}
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      const ok = await confirm({
                                        title: "Excluir arquivo",
                                        description:
                                          "Você realmente deseja excluir este arquivo? Esta ação pode ser irreversível!",
                                        confirmText: "Excluir",
                                        cancelText: "Cancelar",
                                      });
                                      if (ok) {
                                        await handleDelete(item.id);
                                        showToast("Arquivo excluído com sucesso!", { severity: "success" });
                                      }
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                }
                              />
                            </ImageListItem>
                          </a>
                        ))}
                    </ImageList>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum documento encontrado para este evento.
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nenhum evento encontrado.
          </Typography>
        )}
        {/* iframe legado removido */}
      </Paper>
    </>
  );
}

